/**
 * WorkoutRoutineForm Data Mapping Functions
 * Implements transformations between form data and GraphQL mutation/response formats
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */

import type {
  WorkoutRoutineFormData,
  WorkoutGroupFormData,
  WorkoutFormData,
  ExerciseWorkoutFormData,
} from './WorkoutRoutineForm.schema';
import {generateTempId} from './WorkoutRoutineForm.schema';

// ============================================================================
// Mutation Input Types (Form Data → GraphQL)
// ============================================================================

export interface ExerciseWorkoutMutationInput {
  id?: string | number;
  exerciseId: string | number;
  repetitions?: string;
  notes?: string;
  rounds?: string;
  load?: string;
  time?: string;
  timeText?: string;
  restTime?: number;
  order: number;
  _destroy?: boolean;
}

export interface WorkoutMutationInput {
  id?: string | number;
  name: string;
  description?: string;
  timeText?: string;
  checkinEnabled?: boolean;
  order: number;
  _destroy?: boolean;
  exerciseWorkoutsAttributes?: ExerciseWorkoutMutationInput[];
}

export interface WorkoutGroupMutationInput {
  id?: string | number;
  name: string;
  description?: string;
  coverUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  attachmentUrl?: string;
  order: number;
  _destroy?: boolean;
  workoutsAttributes?: WorkoutMutationInput[];
}

export interface WorkoutRoutineMutationInput {
  id?: string;
  name: string;
  startDate: string;
  endDate: string;
  cycleType: 'user' | 'date';
  isDraft?: boolean;
  showOnlyOnScheduledDays?: boolean;
  target: 'programs' | 'users';
  commentEnable?: boolean;
  programIds?: (string | number)[];
  usersTargetIds?: (string | number)[];
  weekDayIds?: (string | number)[];
  workoutGroups?: WorkoutGroupMutationInput[];
}

// ============================================================================
// GraphQL Response Types (GraphQL → Form Data)
// ============================================================================

export interface ExerciseWorkoutResponse {
  id: string | number;
  exerciseId?: string | number;
  repetitions?: string;
  notes?: string;
  rounds?: string;
  load?: string;
  time?: string;
  timeText?: string;
  restTime?: string | number;
  order?: number;
  exercise?: {
    id: string | number;
    name: string;
    value?: string | number;
    label?: string;
  };
}

export interface WorkoutResponse {
  id: string | number;
  name: string;
  description?: string;
  time?: string;
  timeText?: string;
  checkinEnabled?: boolean;
  ordering?: number;
  exerciseWorkouts?: ExerciseWorkoutResponse[];
}

export interface WorkoutGroupResponse {
  id: string | number;
  name: string;
  description?: string;
  cover?: {url?: string};
  video?: {url?: string};
  audio?: {url?: string};
  attachment?: {url?: string};
  workouts?: WorkoutResponse[];
}

export interface WorkoutRoutineResponse {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  cycleType?: 'user' | 'date' | null;
  isDraft?: boolean;
  showOnlyOnScheduledDays?: boolean;
  target?: 'programs' | 'users';
  commentEnable?: boolean;
  programIds?: (string | number)[];
  programsSelected?: Array<{
    id?: string | number;
    value: string | number;
    label: string;
  }>;
  usersTargetIds?: (string | number)[];
  usersTarget?: Array<{
    id: string | number;
    name: string;
    email?: string;
    value?: string | number;
    label?: string;
  }>;
  weekDayIds?: (string | number)[];
  workoutGroups?: WorkoutGroupResponse[];
}

// ============================================================================
// GraphQL Error Types
// ============================================================================

export interface GraphQLFieldError {
  path?: string;
  field?: string;
  message: string;
}

export interface GraphQLError {
  message: string;
  path?: string[];
  extensions?: {
    field?: string;
    code?: string;
  };
}

// ============================================================================
// Transform Functions: Form Data → GraphQL Mutation Input
// ============================================================================

/**
 * Parse time text (MM:SS) to seconds
 */
function parseTimeTextToSeconds(timeText?: string): string | undefined {
  if (!timeText) return undefined;

  const parts = timeText.split(':');
  if (parts.length <= 1) return timeText;

  const minutes = parseInt(parts[0], 10) || 0;
  const seconds = parseInt(parts[1], 10) || 0;

  return String(minutes * 60 + seconds);
}

/**
 * Parse rest time to number
 */
function parseRestTime(restTime?: string | number): number | undefined {
  if (restTime === '' || restTime === undefined || restTime === null) {
    return undefined;
  }
  if (typeof restTime === 'number') {
    return restTime;
  }
  const parsed = parseInt(restTime, 10);
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Transform ExerciseWorkout form data to mutation input
 * Requirements: 5.1 - Transform data to Rails nested attributes format
 */
export function transformExerciseWorkoutToMutation(
  exercise: ExerciseWorkoutFormData,
  order: number,
): ExerciseWorkoutMutationInput {
  // Resolve exerciseId from either direct field or exercise object
  let exerciseId = exercise.exerciseId;
  if (!exerciseId && exercise.exercise) {
    exerciseId = exercise.exercise.value;
  }

  return {
    id: exercise.id,
    exerciseId,
    repetitions: exercise.repetitions || undefined,
    notes: exercise.notes || undefined,
    rounds: exercise.rounds || undefined,
    load: exercise.load || undefined,
    time: parseTimeTextToSeconds(exercise.timeText || exercise.time),
    timeText: exercise.timeText || undefined,
    restTime: parseRestTime(exercise.restTime),
    order,
    _destroy: exercise._destroy,
  };
}

/**
 * Transform Workout form data to mutation input
 * Requirements: 5.1 - Transform data to Rails nested attributes format
 */
export function transformWorkoutToMutation(
  workout: WorkoutFormData,
  order: number,
): WorkoutMutationInput {
  return {
    id: workout.id,
    name: workout.name,
    description: workout.description || undefined,
    timeText: workout.time_text || undefined,
    checkinEnabled: workout.checkinEnabled,
    order,
    _destroy: workout._destroy,
    exerciseWorkoutsAttributes: workout.exerciseWorkouts?.map(
      (exercise, index) => transformExerciseWorkoutToMutation(exercise, index),
    ),
  };
}

/**
 * Transform WorkoutGroup form data to mutation input
 * Requirements: 5.1 - Transform data to Rails nested attributes format
 */
export function transformWorkoutGroupToMutation(
  group: WorkoutGroupFormData,
  order: number,
): WorkoutGroupMutationInput {
  // Extract URL strings from media objects if present
  const coverUrl =
    typeof group.coverUrl === 'string' ? group.coverUrl : group.cover?.url;
  const videoUrl =
    typeof group.videoUrl === 'string' ? group.videoUrl : group.video?.url;
  const audioUrl =
    typeof group.audioUrl === 'string' ? group.audioUrl : group.audio?.url;
  const attachmentUrl =
    typeof group.attachmentUrl === 'string'
      ? group.attachmentUrl
      : group.attachment?.url;

  return {
    id: group.id,
    name: group.name,
    description: group.description || undefined,
    coverUrl,
    videoUrl,
    audioUrl,
    attachmentUrl,
    order,
    _destroy: group._destroy,
    workoutsAttributes: group.workouts?.map((workout, index) =>
      transformWorkoutToMutation(workout, index),
    ),
  };
}

/**
 * Transform complete WorkoutRoutine form data to mutation input
 * Requirements: 5.1 - Transform data to Rails nested attributes format
 * Requirements: 5.3 - Preserve existing IDs when updating records
 */
export function transformToMutation(
  formData: WorkoutRoutineFormData,
): WorkoutRoutineMutationInput {
  return {
    id: formData.id,
    name: formData.name,
    startDate: formData.startDate,
    endDate: formData.endDate,
    cycleType: formData.cycleType,
    isDraft: formData.isDraft,
    showOnlyOnScheduledDays: formData.showOnlyOnScheduledDays,
    target: formData.target,
    commentEnable: formData.commentEnable,
    programIds: formData.target === 'programs' ? formData.programIds : [],
    usersTargetIds: formData.target === 'users' ? formData.usersTargetIds : [],
    weekDayIds: formData.weekDayIds,
    workoutGroups: formData.workoutGroups?.map((group, index) =>
      transformWorkoutGroupToMutation(group, index),
    ),
  };
}

// ============================================================================
// Transform Functions: GraphQL Response → Form Data
// ============================================================================

/**
 * Format date to YYYY-MM-DD string
 */
const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Transform ExerciseWorkout response to form data
 * Requirements: 5.2 - Handle _destroy flags for soft deletion
 */
export function transformExerciseWorkoutFromResponse(
  exercise: ExerciseWorkoutResponse,
): ExerciseWorkoutFormData {
  // Build exercise select option from response
  const exerciseOption = exercise.exercise
    ? {
        value: exercise.exercise.value || exercise.exercise.id,
        label: exercise.exercise.label || exercise.exercise.name,
      }
    : undefined;

  return {
    id: exercise.id,
    tempId: undefined,
    exerciseId: exercise.exerciseId || exercise.exercise?.id || '',
    exercise: exerciseOption,
    repetitions: exercise.repetitions,
    notes: exercise.notes,
    rounds: exercise.rounds,
    load: exercise.load,
    time: exercise.time,
    timeText: exercise.timeText,
    restTime: exercise.restTime,
    order: exercise.order,
    _destroy: false,
  };
}

/**
 * Transform Workout response to form data
 */
export function transformWorkoutFromResponse(
  workout: WorkoutResponse,
): WorkoutFormData {
  return {
    id: workout.id,
    tempId: undefined,
    name: workout.name,
    description: workout.description,
    time: workout.time,
    time_text: workout.timeText,
    checkinEnabled: workout.checkinEnabled ?? false,
    exerciseWorkouts:
      workout.exerciseWorkouts?.map(transformExerciseWorkoutFromResponse) ?? [],
    order: workout.ordering,
    _destroy: false,
  };
}

/**
 * Transform WorkoutGroup response to form data
 */
export function transformWorkoutGroupFromResponse(
  group: WorkoutGroupResponse,
): WorkoutGroupFormData {
  return {
    id: group.id,
    tempId: undefined,
    name: group.name,
    description: group.description,
    workouts: group.workouts?.map(transformWorkoutFromResponse) ?? [],
    coverUrl: group.cover?.url,
    videoUrl: group.video?.url,
    audioUrl: group.audio?.url,
    attachmentUrl: group.attachment?.url,
    cover: group.cover,
    video: group.video,
    audio: group.audio,
    attachment: group.attachment,
    order: undefined,
    _destroy: false,
  };
}

/**
 * Transform complete WorkoutRoutine response to form data
 * Requirements: 5.3 - Preserve existing IDs when updating records
 */
export function transformFromResponse(
  response: WorkoutRoutineResponse,
): WorkoutRoutineFormData {
  return {
    id: response.id,
    name: response.name,
    startDate: response.startDate || formatDate(new Date()),
    endDate: response.endDate || formatDate(new Date()),
    cycleType: response.cycleType || 'date',
    isDraft: response.isDraft,
    showOnlyOnScheduledDays: response.showOnlyOnScheduledDays,
    target: response.target || 'programs',
    commentEnable: response.commentEnable ?? true,
    programIds: response.programIds ?? [],
    programsSelected:
      response.programsSelected?.map((p) => ({
        value: p.value || p.id || '',
        label: p.label,
      })) ?? [],
    usersTargetIds: response.usersTargetIds ?? [],
    usersTarget: response.usersTarget ?? [],
    weekDayIds: response.weekDayIds ?? [],
    showDaysOfWeek: (response.weekDayIds?.length ?? 0) > 0,
    workoutGroups:
      response.workoutGroups?.map(transformWorkoutGroupFromResponse) ?? [],
  };
}

// ============================================================================
// Error Mapping Functions
// ============================================================================

/**
 * Convert Rails-style error path to React Hook Form path
 * e.g., "workout_groups.0.name" → "workoutGroups.0.name"
 * e.g., "workout_groups[0].workouts[1].name" → "workoutGroups.0.workouts.1.name"
 */
export function convertRailsPathToFormPath(railsPath: string): string {
  return (
    railsPath
      // Convert snake_case to camelCase for known fields
      .replace(/workout_groups/g, 'workoutGroups')
      .replace(/workouts_attributes/g, 'workouts')
      .replace(/exercise_workouts_attributes/g, 'exerciseWorkouts')
      .replace(/exercise_workouts/g, 'exerciseWorkouts')
      .replace(/time_text/g, 'time_text') // Keep as is for form field
      .replace(/checkin_enabled/g, 'checkinEnabled')
      .replace(/rest_time/g, 'restTime')
      .replace(/exercise_id/g, 'exerciseId')
      .replace(/start_date/g, 'startDate')
      .replace(/end_date/g, 'endDate')
      .replace(/cycle_type/g, 'cycleType')
      .replace(/is_draft/g, 'isDraft')
      .replace(/comment_enable/g, 'commentEnable')
      .replace(/program_ids/g, 'programIds')
      .replace(/users_target_ids/g, 'usersTargetIds')
      .replace(/week_day_ids/g, 'weekDayIds')
      .replace(/show_only_on_scheduled_days/g, 'showOnlyOnScheduledDays')
      // Convert bracket notation to dot notation
      .replace(/\[(\d+)\]/g, '.$1')
  );
}

/**
 * Map GraphQL errors to form field errors
 * Requirements: 5.4 - Map backend errors to corresponding form fields
 *
 * @param errors - Array of GraphQL errors
 * @returns Record of field paths to error messages
 */
export function mapBackendErrors(
  errors: GraphQLError[],
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  for (const error of errors) {
    // Try to extract field path from various error formats
    let fieldPath: string | undefined;

    // Check extensions.field first (common in Rails GraphQL)
    if (error.extensions?.field) {
      fieldPath = error.extensions.field;
    }
    // Check path array (Apollo-style)
    else if (error.path && error.path.length > 0) {
      fieldPath = error.path.join('.');
    }

    if (fieldPath) {
      const formPath = convertRailsPathToFormPath(fieldPath);
      fieldErrors[formPath] = error.message;
    } else {
      // General form error if no specific field
      fieldErrors['_form'] = error.message;
    }
  }

  return fieldErrors;
}

/**
 * Map field-level errors from GraphQL response
 * Handles the common pattern where errors are returned as an array of {field, message}
 */
export function mapFieldErrors(
  errors: GraphQLFieldError[],
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  for (const error of errors) {
    const fieldPath = error.path || error.field;
    if (fieldPath) {
      const formPath = convertRailsPathToFormPath(fieldPath);
      fieldErrors[formPath] = error.message;
    } else {
      fieldErrors['_form'] = error.message;
    }
  }

  return fieldErrors;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if two form data objects are equivalent (for round-trip testing)
 * Ignores tempId fields and order differences
 */
export function areFormDataEquivalent(
  a: WorkoutRoutineFormData,
  b: WorkoutRoutineFormData,
): boolean {
  // Compare root level fields
  if (
    a.id !== b.id ||
    a.name !== b.name ||
    a.startDate !== b.startDate ||
    a.endDate !== b.endDate ||
    a.cycleType !== b.cycleType ||
    a.target !== b.target
  ) {
    return false;
  }

  // Compare workout groups count
  if (a.workoutGroups.length !== b.workoutGroups.length) {
    return false;
  }

  // Compare each workout group
  for (let i = 0; i < a.workoutGroups.length; i++) {
    const groupA = a.workoutGroups[i];
    const groupB = b.workoutGroups[i];

    if (groupA.id !== groupB.id || groupA.name !== groupB.name) {
      return false;
    }

    // Compare workouts
    if ((groupA.workouts?.length ?? 0) !== (groupB.workouts?.length ?? 0)) {
      return false;
    }

    for (let j = 0; j < (groupA.workouts?.length ?? 0); j++) {
      const workoutA = groupA.workouts![j];
      const workoutB = groupB.workouts![j];

      if (workoutA.id !== workoutB.id || workoutA.name !== workoutB.name) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Extract all IDs from form data (for ID preservation testing)
 */
export function extractAllIds(
  formData: WorkoutRoutineFormData,
): Array<{type: string; id: string | number}> {
  const ids: Array<{type: string; id: string | number}> = [];

  if (formData.id) {
    ids.push({type: 'routine', id: formData.id});
  }

  for (const group of formData.workoutGroups) {
    if (group.id) {
      ids.push({type: 'workoutGroup', id: group.id});
    }

    for (const workout of group.workouts ?? []) {
      if (workout.id) {
        ids.push({type: 'workout', id: workout.id});
      }

      for (const exercise of workout.exerciseWorkouts ?? []) {
        if (exercise.id) {
          ids.push({type: 'exerciseWorkout', id: exercise.id});
        }
      }
    }
  }

  return ids;
}

/**
 * Extract all IDs from mutation input (for ID preservation testing)
 */
export function extractAllIdsFromMutation(
  mutation: WorkoutRoutineMutationInput,
): Array<{type: string; id: string | number}> {
  const ids: Array<{type: string; id: string | number}> = [];

  if (mutation.id) {
    ids.push({type: 'routine', id: mutation.id});
  }

  for (const group of mutation.workoutGroups ?? []) {
    if (group.id) {
      ids.push({type: 'workoutGroup', id: group.id});
    }

    for (const workout of group.workoutsAttributes ?? []) {
      if (workout.id) {
        ids.push({type: 'workout', id: workout.id});
      }

      for (const exercise of workout.exerciseWorkoutsAttributes ?? []) {
        if (exercise.id) {
          ids.push({type: 'exerciseWorkout', id: exercise.id});
        }
      }
    }
  }

  return ids;
}
